// The Community ID field's rules, pure — the donor island's
// (`timmy/src/domains/communities/islands/CommunityIdAutocompleteIsland/
// community-id-autocomplete-island.tsx`, cited as ISL) and its helpers', kept
// out of the component so each rule is unit-testable without a browser (#708).
//
// What lives here: the search-term processing the query sends, the two match
// rules the field resolves by, the option label and its bold runs, the three
// validation rules, and the island's state derivation with its helper-text
// precedence. What does not: the debounce, the query and the open/close
// mechanics, which are the component's.

import { REMOVE_ACCENTS_MAP } from "./remove-accents-map";

/** A community as the field reads it — one row of the search. */
export interface CommunityOption {
  id: number;
  name: string;
  externalFullId: string;
  externalCompositeId: string;
  divisionId: number;
}

/** The island's strings, verbatim (ISL:28,195-198,230). */
export const COMMUNITY_ID_MESSAGES = {
  required: "Required field",
  pattern: "Community ID must be 4 to 7 numeric characters or match a valid JDE Community ID",
  ambiguous: "More than one Community starts with this ID. Select one from the list.",
  noMatch: "ID does not match any Community in JDE",
} as const;

/**
 * The term the search sends — the source's `processSearchTerm`
 * (`communities/shared/data/search-term-helper.ts`), rule for rule.
 *
 * A full id is a composite plus `0000`, and the source's search once had to be
 * sent the composite, so a long numeric term sheds its trailing zeros. The
 * rules are the source's exactly, including the eight-digit single-zero
 * exception, because they decide which request a pasted id makes.
 */
export function processSearchTerm(term: string): string {
  if (!term) return "";
  const trimmed = term.trim();
  if (!/^\d+$/.test(trimmed)) return trimmed;
  if (trimmed.length < 7) return trimmed;
  if (trimmed.length === 8 && trimmed.endsWith("0") && !trimmed.endsWith("00")) return trimmed;
  if (trimmed.endsWith("0000")) return trimmed.slice(0, -4);
  if (trimmed.length >= 8 && /^0+$/.test(trimmed.substring(7))) return trimmed.substring(0, 7);
  return trimmed;
}

/**
 * The option whose composite id or full id **is** `value` (ISL:34-39).
 *
 * Strict equality, as the source's `===`: case-sensitive and untrimmed, so a
 * typed space never matches even though the search term is trimmed.
 */
export function findExactMatch(
  options: readonly CommunityOption[],
  value: string,
): CommunityOption | null {
  return (
    options.find(
      (option) => option.externalCompositeId === value || option.externalFullId === value,
    ) ?? null
  );
}

/**
 * The exact match, unless another option's composite or full id starts with
 * `value` (ISL:41-58).
 *
 * A community code is 3 to 5 digits and a phase is always 2, so a phaseless
 * community's composite is also the start of its phased siblings'. Resolving
 * that while the user is still typing would strand them on the shorter one.
 */
export function findUnambiguousMatch(
  options: readonly CommunityOption[],
  value: string,
): CommunityOption | null {
  const exact = findExactMatch(options, value);
  if (!exact) return null;
  const extended = options.some(
    (option) =>
      option.id !== exact.id &&
      (option.externalCompositeId.startsWith(value) || option.externalFullId.startsWith(value)),
  );
  return extended ? null : exact;
}

/** `<full id> - <name>` — the source's option label (`communitiesApi.ts`). */
export function communityOptionLabel(option: CommunityOption): string {
  return `${option.externalFullId} - ${option.name}`;
}

/**
 * `remove-accents`' `remove`, as `autosuggest-highlight` calls it: one
 * alternation over the library's map keys, in the map's order, each match
 * replaced by its value (`REMOVE_ACCENTS_MAP`). Nothing else folds — no
 * Unicode decomposition — so a mark the map does not list stays in the text.
 */
const ACCENTED = new RegExp(Object.keys(REMOVE_ACCENTS_MAP).join("|"), "g");

function removeAccents(text: string): string {
  return text.replace(ACCENTED, (found) => REMOVE_ACCENTS_MAP[found] ?? found);
}

const REGEX_SPECIALS = /[.*+?^${}()|[\]\\]/g;

/**
 * `autosuggest-highlight/match` at the island's `{ insideWords: true }`
 * (ISL:274): the query split on whitespace, each word found case-blind and
 * accent-blind, anywhere (no word-boundary anchor), its **first** occurrence
 * only, and the ranges sorted by start. The library's own index arithmetic,
 * ported as written, maps each range back from folded text to the label.
 */
function matchRanges(text: string, query: string): [number, number][] {
  // The library's own two spellings: the label folded **per code point** (so
  // an expansion widens only its own slot, which the arithmetic below undoes),
  // the query folded whole.
  const cleanedChars = Array.from(text).map(removeAccents);
  let cleaned = cleanedChars.join("");
  return removeAccents(query)
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .reduce<[number, number][]>((ranges, word) => {
      const length = word.length;
      const found = new RegExp(word.replace(REGEX_SPECIALS, "\\$&"), "i").exec(cleaned);
      if (found) {
        const index = found.index;
        const wordOffset = length - cleanedChars.slice(index, index + length).join("").length;
        const initialOffset = index - cleanedChars.slice(0, index).join("").length;
        const range: [number, number] = [
          index + initialOffset,
          index + length + initialOffset + wordOffset,
        ];
        if (range[0] !== range[1]) ranges.push(range);
        // Blank the run out, so a later word cannot take the same letters.
        cleaned = cleaned.slice(0, index) + " ".repeat(length) + cleaned.slice(index + length);
      }
      return ranges;
    }, [])
    .sort((a, b) => a[0] - b[0]);
}

export interface HighlightPart {
  text: string;
  highlight: boolean;
}

/**
 * The option label cut into plain and bold runs —
 * `parse(label, match(label, inputValue, { insideWords: true }))` (ISL:274-275),
 * without the dependency. The bold runs render at 700, the rest at 400.
 */
export function highlightParts(label: string, inputValue: string): HighlightPart[] {
  const ranges = matchRanges(label, inputValue);
  if (ranges.length === 0) return [{ text: label, highlight: false }];

  const parts: HighlightPart[] = [];
  if (ranges[0]![0] > 0) parts.push({ text: label.slice(0, ranges[0]![0]), highlight: false });
  ranges.forEach(([start, end], index) => {
    parts.push({ text: label.slice(start, end), highlight: true });
    const next = ranges[index + 1];
    if (next === undefined) {
      if (end < label.length) parts.push({ text: label.slice(end), highlight: false });
    } else if (end < next[0]) {
      parts.push({ text: label.slice(end, next[0]), highlight: false });
    }
  });
  return parts;
}

/**
 * The field's three rules, in react-hook-form's order — `required`, then
 * `pattern`, then `validate` (ISL:194-201). The first that fails names the
 * message; `null` is valid.
 *
 * The value is what the form holds: the composite id once matched (which the
 * 4–7-digit pattern always admits), the typed text otherwise. An id JDE does
 * not know passes — "ID does not match any Community in JDE" is a warning,
 * never a gate.
 */
export function validateCommunityId(
  value: string,
  { ambiguous }: { ambiguous: boolean },
): string | null {
  if (value === "") return COMMUNITY_ID_MESSAGES.required;
  if (!/^[0-9]{4,7}$/.test(value)) return COMMUNITY_ID_MESSAGES.pattern;
  if (ambiguous) return COMMUNITY_ID_MESSAGES.ambiguous;
  return null;
}

export interface CommunityFieldState {
  options: readonly CommunityOption[];
  /** What the form holds: the composite id once matched, else the typed text. */
  value: string;
  matched: boolean;
  /** Whether the user has typed since the field mounted (ISL:87-90). */
  userEdited: boolean;
  isFetching: boolean;
  isError: boolean;
  open: boolean;
  /** The host's submit-time rule message — react-hook-form's `fieldState.error`. */
  error: string | undefined;
}

export interface CommunityFieldStatus {
  ambiguous: boolean;
  noMatch: boolean;
  /** Whether the frame paints in the error colours. */
  error: boolean;
  /** The helper line's text; `null` is the reserved blank line. */
  message: string | null;
}

/**
 * The island's derived state and its helper text (ISL:138-155,222-232).
 *
 * - **Ambiguous**: unmatched, typed, settled, not errored, and the value is an
 *   exact match that another option extends. A value the form arrived with is
 *   never ambiguous — a stored id resolves as it is.
 * - **No match**: unmatched, settled, not errored, the list shut, and not
 *   ambiguous. An empty field qualifies too, as in the source.
 * - **The message**, first that applies: the ambiguity (list shut), the host's
 *   rule message (unmatched), the no-match warning, else the blank line that
 *   keeps the field's height from jumping.
 */
export function communityFieldStatus(state: CommunityFieldState): CommunityFieldStatus {
  const settled = !state.isFetching && !state.isError;
  const ambiguous =
    !state.matched &&
    state.userEdited &&
    settled &&
    findExactMatch(state.options, state.value) !== null &&
    findUnambiguousMatch(state.options, state.value) === null;
  const noMatch = !state.matched && settled && !state.open && !ambiguous;
  const ruleMessage = state.matched ? undefined : state.error;

  return {
    ambiguous,
    noMatch,
    error: ruleMessage !== undefined || noMatch || (ambiguous && !state.open),
    message:
      (ambiguous && !state.open ? COMMUNITY_ID_MESSAGES.ambiguous : null) ??
      ruleMessage ??
      (noMatch ? COMMUNITY_ID_MESSAGES.noMatch : null),
  };
}
